import React, { useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import PropTypes from "prop-types"; // For prop-types validation
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  FormControl,
  InputLabel,
  Select,
  Box,
  MenuItem,
  Dialog,
  DialogContent,
  CircularProgress,
  TableContainer,
  Typography,
  Paper,
} from "@mui/material";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
} from "components/StyledComponents/styledComponents.js";
import { selectScrollStyle } from "components/Styles/globalStyles";
import useBusinessMonthYear from "components/GlobalHooks/useMonthlyBusiness";
import { writeFile, utils } from "xlsx"; // Import XLSX for Excel export
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline"; // Download icon for the button
import { monthEndStyles } from "components/Styles/monthEndStyles";

export default function ProfitAndLoss() {
  const { monthIdData, message, alertType, setAlert, open, handleClose } = useBusinessMonthYear();
  const [rowData, setRowData] = useState([]);
  const [monthId, setMonthId] = useState(0);
  const [loading, setLoading] = useState(false);

  const dummyData = [
    {
      applicationNumber: "APP123456",
      businessType: "Housing Loan",
      appliedAmount: 50000,
      andromedaPayout: 2000,
      dsaPayout: 1500,
      grossProfit: 7000,
      expenses: 3000,
      netProfit: 4000,
      loyalty: 800, // 20% of net profit
    },
    {
      applicationNumber: "APP123457",
      businessType: "Loan Against Property",
      appliedAmount: 75000,
      andromedaPayout: 2500,
      dsaPayout: 1800,
      grossProfit: 9000,
      expenses: 4000,
      netProfit: 5000,
      loyalty: 1000, // 20% of net profit
    },
    {
      applicationNumber: "APP123458",
      businessType: "Education Loan",
      appliedAmount: 62000,
      andromedaPayout: 2200,
      dsaPayout: 1600,
      grossProfit: 8000,
      expenses: 3500,
      netProfit: 4500,
      loyalty: 900, // 20% of net profit
    },
    {
      applicationNumber: "APP123459",
      businessType: "Business Loan",
      appliedAmount: 90000,
      andromedaPayout: 3000,
      dsaPayout: 2000,
      grossProfit: 10000,
      expenses: 5000,
      netProfit: 5000,
      loyalty: 1000, // 20% of net profit
    },
    {
      applicationNumber: "APP123460",
      businessType: "Personal Loan",
      appliedAmount: 80000,
      andromedaPayout: 2800,
      dsaPayout: 2200,
      grossProfit: 9500,
      expenses: 4200,
      netProfit: 5300,
      loyalty: 1060, // 20% of net profit
    },
  ];
  const ActionCellRenderer = (props) => {
    const handleDownload = () => {
      if (!rowData.length) return;
      const wb = utils.book_new();
      const ws = utils.json_to_sheet(rowData); // Convert the entire rowData to a sheet
      utils.book_append_sheet(wb, ws, "EmployeeData");
      writeFile(wb, "Profit_&_Loss .xlsx"); // Save the workbook
    };

    return (
      <StyledButton onClick={handleDownload}>
        <DownloadForOfflineIcon fontSize="medium" />
      </StyledButton>
    );
  };

  // PropTypes validation for props in ActionCellRenderer
  ActionCellRenderer.propTypes = {
    data: PropTypes.shape({
      employeeName: PropTypes.string.isRequired,
    }).isRequired,
  };
  const triggerFetchCall = () => {
    console.log("api call");
  };

  const menuProps = {
    PaperProps: {
      sx: {
        ...selectScrollStyle,
      },
    },
  };
  const columnDefs = [
    {
      headerName: "Application Number",
      field: "applicationNumber",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "Business Type",
      field: "businessType",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
    },
    {
      headerName: "Applied Amount",
      field: "appliedAmount",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`, // Formats and rounds to two decimals
    },
    {
      headerName: "Andromeda Payout",
      field: "andromedaPayout",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`, // Formats and rounds to two decimals
    },
    {
      headerName: "DSA Payout",
      field: "dsaPayout",
      filter: true,
      minWidth: 150,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`, // Formats and rounds to two decimals
    },
    {
      headerName: "Gross Profit",
      field: "grossProfit",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`, // Formats and rounds to two decimals
    },
    {
      headerName: "Expenses",
      field: "expenses",
      filter: true,
      sortable: true,
      minWidth: 120,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`, // Formats and rounds to two decimals
    },
    {
      headerName: "Net Profit",
      field: "netProfit",
      minWidth: 120,
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`, // Formats and rounds to two decimals
    },
    {
      headerName: "Loyalty (20%)",
      field: "loyalty",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`, // Formats and rounds to two decimals
    },
  ];

  return (
    <DashboardLayout>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
      >
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box sx={monthEndStyles.formContainer}>
          <Box sx={monthEndStyles.formItem}>
            <FormControl sx={monthEndStyles.yearField}>
              <InputLabel sx={monthEndStyles.label}> Date </InputLabel>
              <Select
                labelId="date-select-label"
                value={monthId}
                onChange={(e) => setMonthId(e.target.value)}
                sx={monthEndStyles.yearField}
                MenuProps={menuProps}
              >
                {monthIdData.map((data) => {
                  return (
                    <MenuItem key={data.businessMonthID} value={data.businessMonthID}>
                      {data.date}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <StyledButton onClick={triggerFetchCall}>Get</StyledButton>
          </Box>
          {ActionCellRenderer()}
        </Box>
        <TableContainer component={Paper} className={`ag-theme-alpine`}>
          <AgGridReact
            rowData={dummyData}
            columnDefs={columnDefs}
            pagination={true} // Enables pagination
            paginationPageSize={10} // Limits page size to 10 rows per page
          />
        </TableContainer>
      </TableContainer>
    </DashboardLayout>
  );
}
